import { useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { brand } from '@/brand';
import { useLogin } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import CheckboxCommon from '@/shared/ui/Checkbox/CheckboxCommon';
import { AuthInput } from './ui/AuthInput';
import { AuthLayout } from './ui/AuthLayout';
import { authFormClassName, authServerErrorClassName } from './ui/authForm';
import { RecaptchaField } from './RecaptchaField';

// Полноценная мигрированная Login-страница с двухколоночным layout-ом из
// snapshot WapClick-фронтендера (mcms-front/src/pages/Auth/Login.tsx).
//
// 2FA-флоу: useLogin кладёт pending2fa в RTK store при outcome 'need-2fa';
// после navigate на /auth/2fa мигрированная TwoFactorAuth-страница читает
// pending2fa из RTK store через useAppSelector и продолжает поток.

// Фабрика, а не готовая схема: сообщения валидации приходят из словаря, и
// собранная при импорте схема осталась бы на языке первого рендера.
const createLoginSchema = (t: TFunction<'auth'>) =>
  z.object({
    email: z.string().min(1, t('validation.emailRequired')),
    password: z.string().min(1, t('validation.passwordRequired')),
    rememberMe: z.boolean(),
  });

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

export function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const loginSchema = useMemo(() => createLoginSchema(t), [t]);
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const loginMutation = useLogin();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
    watch,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const rememberMeValue = watch('rememberMe');
  const handleRememberMeChange = (checked: boolean) => {
    setValue('rememberMe', checked);
  };

  // Сбрасывает server-error при наборе. Воспроизводит UX mcms-front.
  const handleInputChange = (field: keyof LoginFormData) => {
    if (field !== 'rememberMe' && errors.root?.server) {
      clearErrors('root.server');
    }
    if (errors[field]) {
      clearErrors(field);
    }
  };

  const getInputShowError = (fieldName: keyof LoginFormData) => {
    return !!errors[fieldName] || !!errors.root?.server;
  };

  const onSubmit = async (data: LoginFormData) => {
    clearErrors('root.server');
    const reCaptcha = recaptchaRef.current?.getValue();
    if (!reCaptcha) {
      setFormError('root.server', {
        type: 'manual',
        message: t('validation.recaptcha'),
      });
      return;
    }
    try {
      const outcome = await loginMutation.mutateAsync({
        username: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        reCaptcha,
      });
      switch (outcome.kind) {
        case 'authenticated':
          // useLogin синхронно ставит setAuthenticated в RTK — SPA-navigate
          // безопасен, RedirectIfAuthed/guards увидят authenticated.
          navigate('/', { replace: true });
          break;
        case 'need-2fa':
          // TwoFactorAuth страница читает pending2fa из RTK store (положен
          // useLogin). Soft-навигация (через react-router) допустима, потому
          // что обе страницы на новом стеке.
          navigate('/auth/2fa', { replace: true });
          break;
        case 'not-activated':
          setFormError('root.server', {
            type: 'manual',
            // Куда посылать партнёра — зависит от того, шлёт ли инстанс письмо
            // с подтверждением (brand.features.emailActivation). Там, где не
            // шлёт, аккаунт активирует менеджер, и совет «подтвердите email»
            // отправлял бы искать несуществующее письмо.
            message: brand.features.emailActivation
              ? t('login.notActivatedEmail')
              : t('login.notActivatedManager'),
          });
          recaptchaRef.current?.reset();
          break;
        case 'validation':
          recaptchaRef.current?.reset();
          if (outcome.errors.username?.[0] || outcome.errors.email?.[0]) {
            setFormError('email', {
              type: 'server',
              message: outcome.errors.username?.[0] ?? outcome.errors.email?.[0],
            });
          }
          if (outcome.errors.password?.[0]) {
            setFormError('password', {
              type: 'server',
              message: outcome.errors.password[0],
            });
          }
          if (outcome.errors.reCaptcha?.[0]) {
            setFormError('root.server', {
              type: 'server',
              message: outcome.errors.reCaptcha[0],
            });
          }
          if (Object.keys(outcome.errors).length === 0) {
            setFormError('root.server', {
              type: 'manual',
              message: t('login.invalidCredentials'),
            });
          }
          break;
      }
    } catch {
      recaptchaRef.current?.reset();
      setFormError('root.server', {
        type: 'manual',
        message: t('common:networkError'),
      });
    }
  };

  return (
    <AuthLayout
      title={t('login.title')}
      questionText={t('noAccount')}
      linkTo="/register"
      linkText={t('signUp')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={authFormClassName()}
      >
        {errors.root?.server && (
          <div className={authServerErrorClassName()}>
            {errors.root.server.message}
          </div>
        )}

        <div className="relative w-full">
          <AuthInput
            type="email"
            autoComplete="email"
            label={t('fields.email')}
            example={t('examples.email')}
            error={errors.email?.message}
            showError={getInputShowError('email')}
            {...register('email', {
              onChange: () => handleInputChange('email'),
            })}
          />
        </div>

        <div className="relative w-full">
          <AuthInput
            type="password"
            autoComplete="current-password"
            label={t('fields.password')}
            example={t('examples.password')}
            error={errors.password?.message}
            showError={getInputShowError('password')}
            {...register('password', {
              onChange: () => handleInputChange('password'),
            })}
          />
        </div>

        <div className="flex justify-between">
          <div className="flex items-center gap-1">
            <CheckboxCommon
              checked={rememberMeValue}
              onChange={handleRememberMeChange}
              color="primary"
              label={t('rememberMe')}
              colorLabel="content-auth-checkbox"
            />
          </div>
          <Link
            to="/forgot-password"
            className="text-content-auth-link hover:text-content-auth-link-hover"
          >
            {t('login.forgot')}
          </Link>
        </div>

        <div>
          <RecaptchaField ref={recaptchaRef} />
        </div>

        <div className="mt-[6px]">
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="min-h-[48px] w-full !px-0"
          >
            {isSubmitting ? t('login.submitting') : t('signIn')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
